import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import { isEmpty } from 'lodash';
import styled from 'styled-components';

import { horizontalSpace } from '@doltech/utils/lib/css-style';

import { useRootElement } from '../../../slatePlugins/common/hocs/current-element.store';
import { findBellowByType } from '../../../figma/DolEditor/helpers/nodeValue';
import { showToast } from '../../../figma/Toast';
import { ResponsiveTypography } from '../../../figma/Typography/v3/ResponsiveTypography';
import { QuestionNumberInlineItem } from '../../../figma/QuestionNumberInlineItem/QuestionNumberInlineItem';
import { ExerciseBlockFieldItemText } from '../../../figma/BlogEditorBlogSystem/ExerciseBlockFieldItem/ExerciseBlockFieldItemText/ExerciseBlockFieldItemText';

import { CORRECT_ANSWER, BLANK_V2 } from '../../utils/types';
import { getCheckCorrectAnswerFreeInput } from '../../utils/getCheckCorrectAnswerFreeInput';
import { useProgressListGrammar } from '../../hocs/progress-list-grammar';

const InputText = styled.span`
  display: inline;
  ${horizontalSpace(4)}

  input {
    min-width: 78px;
  }

  &.result {
    input {
      min-width: auto;
    }
  }
`;

const Container = styled.span`
  display: inline;
  ${horizontalSpace(4)}

  margin: 0 6px;
`;

export const GapFillBlank = (props: RenderElementProps) => {
  const { attributes, children, element } = props;
  const progressStore = useProgressListGrammar();
  const rootElement = useRootElement();
  const { id: questionId } = element;

  const correctAnswerNode = findBellowByType(rootElement, CORRECT_ANSWER)
    .find((eachNode) => eachNode.forId === questionId);
  const correctAnswerText = correctAnswerNode?.correctAnswer;

  const blanks = findBellowByType(rootElement, BLANK_V2);
  const blankIndex = blanks.findIndex((blank) => blank.id === element.id);
  const blankOrder = blankIndex + (rootElement?.startFrom ?? 1);

  const currentUserAnswer = progressStore?.userAnswers?.find(item => item?.questionId === questionId)?.answer;

  const {
    isMatch,
    correctAnswerToView,
    userAnswerToView,
  } = getCheckCorrectAnswerFreeInput(correctAnswerText, currentUserAnswer);

  const isMissed = isEmpty(currentUserAnswer);
  const isCorrect = isMatch;

  const handleUserAnswers = (answer) => {
    progressStore.setUserAnswers(prevUserAnswers => {
      const updatedAnswer = {
        questionId,
        answer,
      };
      const existUserAnswer = prevUserAnswers?.find(eachAnswer => eachAnswer?.questionId === questionId);

      if (existUserAnswer) {
        return prevUserAnswers?.map(eachAnswer => {
          if (questionId === eachAnswer?.questionId) {
            return updatedAnswer;
          }
          return eachAnswer;
        });
      }
      return [
        ...prevUserAnswers,
        updatedAnswer,
      ];
    });
  };

  if (progressStore?.isShowResult) {
    if (isMissed) {
      return (
        <Container className="gap-fill-blank missed" {...attributes}>
          <QuestionNumberInlineItem order={blankOrder} />
          <InputText className="result">
            <ExerciseBlockFieldItemText
              status="correct"
              value={correctAnswerToView}
              readonly
            />
            <ResponsiveTypography.Paragraph variant="regular/16-24" color="neutral120">
              Missed
            </ResponsiveTypography.Paragraph>
          </InputText>
          {children}
        </Container>
      );
    }

    if (isCorrect) {
      return (
        <Container className="gap-fill-blank correct" {...attributes}>
          <QuestionNumberInlineItem order={blankOrder} />
          <InputText className="result">
            <ExerciseBlockFieldItemText
              status="correct"
              value={userAnswerToView}
              readonly
            />
          </InputText>
          {children}
        </Container>
      );
    }

    return (
      <Container className="gap-fill-blank incorrect" {...attributes}>
        <QuestionNumberInlineItem order={blankOrder} />
        <InputText className="result">
          <ExerciseBlockFieldItemText
            status="incorrect"
            value={userAnswerToView}
            readonly
          />
          <ExerciseBlockFieldItemText
            status="correct"
            value={correctAnswerToView}
            readonly
          />
        </InputText>
        {children}
      </Container>
    );
  }

  return (
    <Container className="gap-fill-blank" {...attributes}>
      <QuestionNumberInlineItem order={blankOrder} />
      <InputText>
        <ExerciseBlockFieldItemText
          value={currentUserAnswer}
          onChange={handleUserAnswers}
          onPaste={(e) => {
            e.preventDefault();
            showToast({
              icon: 'error',
              title: 'Không thể paste từ nguồn khác vào',
            });
          }}
        />
      </InputText>
      {children}
    </Container>
  );
};
